import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/base/FilmCard";
import EmptyState from "@/components/base/EmptyState";
import { formatDuration, formatViews, getFilmById, relateFilms, topRatedFilms } from "@/lib/film";
import { useDemoLoading } from "@/hooks/useDemoLoading";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const film = getFilmById(id);
  const loading = useDemoLoading(260);
  const [shareTip, setShareTip] = useState(false);

  useEffect(() => {
    if (!shareTip) return;
    const timer = window.setTimeout(() => setShareTip(false), 2200);
    return () => window.clearTimeout(timer);
  }, [shareTip]);

  usePageMeta({
    title: film ? `${film.title} - 日本一级片` : "影片信息不存在 - 日本一级片",
    description: film
      ? `观看详情介绍及资料，深入了解《${film.title}》日本一级电影作品。`
      : "该影片资料暂不存在，请返回分类页浏览其他日本影片。",
    keywords: film ? `${film.title},${film.originalTitle},${film.genres.join(",")},日本电影` : "日本电影",
  });

  if (!film) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-16">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "影片详情" }]} />
          <div className="mt-6">
            <EmptyState
              title="影片信息不存在"
              description="该影片资料可能已被移除，或链接地址有误。"
              actionLabel="返回分类页"
              onAction={() => navigate("/category")}
              secondaryLabel="返回首页"
              secondaryPath="/"
            />
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = relateFilms(film, 4);
  const fallbackRelated = related.length > 0 ? related : topRatedFilms(4).filter((item) => item.id !== film.id);
  const sideList = fallbackRelated.slice(0, 3);

  if (loading) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <div className="h-4 w-40 rounded skeleton-block mb-8"></div>
          <div className="grid md:grid-cols-[260px_1fr] gap-8">
            <div className="w-full aspect-[2/3] rounded-lg skeleton-block"></div>
            <div className="space-y-4">
              <div className="h-8 w-2/3 rounded skeleton-block"></div>
              <div className="h-4 w-1/3 rounded skeleton-block"></div>
              <div className="h-24 w-full rounded skeleton-block"></div>
              <div className="h-4 w-full rounded skeleton-block"></div>
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const facts = [
    { label: "导演", value: film.director },
    { label: "主演", value: film.cast.join(" / ") },
    { label: "类型", value: film.genres.join(" / ") },
    { label: "上映年份", value: `${film.year} 年` },
    { label: "片长", value: formatDuration(film.duration) },
    { label: "地区", value: film.region },
    { label: "画质", value: film.quality },
    { label: "站内点击", value: `${formatViews(film.views)} 次` },
  ];

  return (
    <SiteLayout>
      <div className="relative border-b border-background-200 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={film.backdrop ?? film.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        <div className="absolute inset-0 bg-background-100/85"></div>

        <div className="relative max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-10">
          <Breadcrumb
            items={[{ label: "首页", path: "/" }, { label: "分类浏览", path: "/category" }, { label: film.title }]}
          />

          <div className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr] gap-6 md:gap-8 lg:gap-10">
            <div className="w-full max-w-[240px] mx-auto md:mx-0 md:max-w-none">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300 bg-background-200">
                <img
                  src={film.cover}
                  alt={`${film.title}（${film.originalTitle}）${film.year} 年日本${film.genres.join("、")}影片封面`}
                  title={`${film.title} 日本${film.genres[0]}影片`}
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-background-50/90 text-[11px] text-foreground-800 font-label">
                  {film.quality}
                </span>
              </div>
              <div className="mt-3 hidden md:flex items-center justify-between text-xs text-foreground-500">
                <span>资料编号</span>
                <span className="font-label">JP-{film.year}-{film.id.slice(0, 3).toUpperCase()}</span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-800 text-[11px] font-label">
                  {film.region}
                </span>
                {film.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    className="px-2 py-0.5 rounded-md bg-background-50 border border-background-300 text-[11px] text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-[34px] leading-tight text-foreground-950">{film.title}</h1>
              <p className="mt-2 text-sm text-foreground-500 font-label tracking-wide">{film.originalTitle}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
                <div className="flex items-end gap-1.5">
                  <span className="font-heading text-3xl md:text-4xl text-primary-700 leading-none">
                    {film.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-500 mb-1">/ 10</span>
                </div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <i
                      key={index}
                      className={`${
                        index < Math.round(film.rating / 2) ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"
                      } text-sm w-4 h-4 flex items-center justify-center`}
                    ></i>
                  ))}
                  <span className="ml-2 text-xs text-foreground-500">站内评分 · 演示数据</span>
                </div>
                <span className="text-xs text-foreground-500 font-label">
                  {film.year} 年 · {film.duration} 分钟 · {film.releaseDate} 上映
                </span>
              </div>

              <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-foreground-700 max-w-[680px]">
                {film.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/category?genre=${encodeURIComponent(film.genres[0])}`}
                  className="h-10 px-5 inline-flex items-center gap-2 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap hover:bg-primary-700 transition-colors"
                >
                  <i className="ri-movie-2-line w-4 h-4 flex items-center justify-center"></i>
                  查看同类影片
                </Link>
                <button
                  type="button"
                  onClick={() => setShareTip(true)}
                  className="relative h-10 px-5 inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                  分享
                  {shareTip && (
                    <span className="absolute -top-9 left-0 px-2.5 py-1.5 rounded-md bg-foreground-900 text-background-50 text-[11px] whitespace-nowrap animate-fade-in">
                      分享功能为演示，暂未接入
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-10">
          <div className="min-w-0">
            <section>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-3">剧情简介</h2>
              <p className="text-sm md:text-[15px] leading-[1.9] text-foreground-700">{film.synopsis}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本片以{film.genres.join("与")}为核心表达，由{film.director}执导，{film.cast[0]}
                领衔主演。影片在{film.region}取景完成，全长 {film.duration} 分钟，{film.year} 年
                {film.releaseDate.slice(5).replace("-", " 月 ")} 日上映。
              </p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-4">影片资料</h2>
              <dl className="rounded-lg border border-background-200 bg-background-50 overflow-hidden divide-y divide-background-200">
                {facts.map((fact) => (
                  <div key={fact.label} className="flex items-start gap-4 px-4 py-3">
                    <dt className="w-16 shrink-0 text-xs text-foreground-500 pt-0.5">{fact.label}</dt>
                    <dd className="text-sm text-foreground-800 leading-relaxed">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-3">影片标签</h2>
              <div className="flex flex-wrap gap-2">
                {film.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category?tag=${encodeURIComponent(tag)}`}
                    className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                  >
                    <i className="ri-hashtag w-3.5 h-3.5 flex items-center justify-center"></i>
                    {tag}
                  </Link>
                ))}
              </div>
              <p className="mt-3 text-xs text-foreground-500">点击标签可进入分类页查看同标签影片。</p>
            </section>
          </div>

          <aside className="lg:sticky lg:top-[88px] h-fit">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base text-foreground-950">站内数据</h2>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-600">累计浏览</span>
                  <span className="text-foreground-900 font-label">{formatViews(film.views)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-600">用户评分</span>
                  <span className="text-foreground-900 font-label">{film.rating.toFixed(1)} / 10</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-600">上映年份</span>
                  <span className="text-foreground-900 font-label">{film.year}</span>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-background-300">
                <p className="text-xs leading-relaxed text-foreground-500">
                  本站仅提供影片资料展示，不提供在线播放与下载。所有数据均为演示内容。
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base text-foreground-950 mb-3">同类推荐</h2>
              <div className="space-y-3">
                {sideList.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-10 h-14 rounded-md overflow-hidden bg-background-200 shrink-0">
                      <img
                        src={item.cover}
                        alt={`${item.title} 影片封面`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5 font-label">
                        {item.year} · {item.genres[0]}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-background-100 border-t border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-end justify-between gap-3 mb-6">
            <div>
              <p className="text-[11px] tracking-[0.24em] text-primary-600 uppercase mb-2 font-label">YOU MAY ALSO LIKE</p>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">相关推荐</h2>
            </div>
            <Link
              to={`/category?genre=${encodeURIComponent(film.genres[0])}`}
              className="inline-flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              更多{film.genres[0]}影片
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {fallbackRelated.map((item) => (
              <FilmCard key={item.id} film={item} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}